// Munkatárs-szerkesztő tiszta segédei (nem-komponens): form-értékek típusa, alapértékek, a @atlas/contracts
// write-sémája (a resolverhez) és a lokalizált zod-hibatérkép. Külön fájl a komponenstől, hogy a react-refresh
// (Fast Refresh) tiszta maradjon. A minta az assets/form.ts — a validálás forrás-igazsága a contract (NEM saját
// séma). Az identity-mag (e-mail/név) MINDKÉT módban szerkeszthető (edit → az Odin identity-core PATCH-e); a
// munkaviszony-adat (positionId + HR-dátumok) szintén. A support-csapat-tagságokat a support-csapat-szerkesztő kezeli.
//
// A DISCORD-ID és az ASANA PAT KIVEZETVE: SZEMÉLYES integrációs adat → a saját „Profilom" oldalon
// állítható. A CREATE viszont kapott egy opcionális KEZDŐ JELSZÓ mezőt: enélkül a felvett személy csak a
// köszöntő-levél linkjével tudott belépni (a szerkesztésnél NINCS jelszó-mező — a csere self-service/elfelejtett-jelszó).
import { EmployeeCreateBodySchema, EmployeeUpdateBodySchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

/**
 * A form mező-értékei (string-alapú). Az identity-mag (email/név) ÉS a munkaviszony-mezők (positionId + HR-dátumok)
 * MINDKÉT módban szerkeszthetők. A felvétel TISZTA GLOBÁLIS provisioning (IdP-modell) → NINCS szerep-választó
 * (azt a Jogosultságok-oldal adja apponként) és NINCS support-csapat-választó (azt a support-szerkesztő kezeli).
 */
export interface EmployeeFormValues {
  email: string;
  givenName: string;
  familyName: string;
  /** Becenév (opcionális) — a `platform.identity_profiles.nickname`. Edit-en előtöltjük (ha az Odin projektálja);
   *  a mentés DIRTY-alapon küldi (érintetlen mező → nem megy → nem nulláz némán meglévő becenevet). */
  nickname: string;
  /** Telefon (opcionális) — a `platform.identity_profiles.phone`, ugyanaz a mező, amit a Profilom ír. */
  phone: string;
  /** Beosztás — KÖTELEZŐ (nincs „nincs beosztás"): üres string = még nincs választva → a resolver required-hibát ad. */
  positionId: string;
  /**
   * HR-DÁTUMOK (`YYYY-MM-DD` vagy üres) — mindkettő OPCIONÁLIS. Nap-pontos érték, tehát nincs zóna-kezelés:
   * a mező azt a napot hordozza, amit a felhasználó választott (a globális `DatePicker` szerződése).
   */
  birthDate: string;
  employmentStartDate: string;
  /** Kilépés napja (`YYYY-MM-DD` vagy üres) — HR-mező. */
  employmentEndDate: string;
  /** Névnap (`YYYY-MM-DD` vagy üres) — személyes adat (a dolgozó a Profilomon maga is írhatja). */
  nameDay: string;
  /** Gyerekek (teljes név + születési dátum, mindkettő kötelező) — személyes adat, mint a névnap. */
  children: { fullName: string; birthDate: string }[];
  /** Foglalkoztatási forma (#43) — alapból alkalmazott. */
  employmentType: 'employee' | 'contractor';
  /** A munkáltató cég (#43) — alapból MORGENS Design Kft. (`MOR`). */
  employerKey: 'MOR' | 'MDIG';
  /** Próbaidős-e (#111) — kikapcsolva a két dátum nem megy (a mentés `null`-lal üríti). */
  probation: boolean;
  /** A próbaidő kezdete és lejárata (`YYYY-MM-DD` vagy üres) — opcionális. */
  probationStartDate: string;
  probationEndDate: string;
  /**
   * KEZDŐ JELSZÓ — CSAK a FELVÉTELNÉL (write-only, üresen hagyható). Üres → a belépést a köszöntő-levél
   * egyszer használatos linkje adja (a biztonságosabb, alapértelmezett út); kitöltve a kolléga azonnal be tud
   * lépni. A TARTALMI policy (min. 12, kis+nagy+szám) autoritása a szerver — a mező-hiba onnan jön, gépi kóddal.
   * SZERKESZTÉSNÉL a mező nem jelenik meg (a csere self-service: „Profilom" vagy elfelejtett jelszó).
   */
  password: string;
}

/** Üres alapértékek (a reset a szerkesztendő sorból tölti fel; create → üres). */
export const EMPTY_EMPLOYEE_FORM: EmployeeFormValues = {
  email: '',
  givenName: '',
  familyName: '',
  nickname: '',
  phone: '',
  // Üres = még nincs választva (a required select placeholderrel indul; a resolver required-hibát ad, ha marad).
  positionId: '',
  birthDate: '',
  employmentStartDate: '',
  employmentEndDate: '',
  nameDay: '',
  children: [],
  employmentType: 'employee',
  employerKey: 'MOR',
  probation: false,
  probationStartDate: '',
  probationEndDate: '',
  password: '',
};

/** A contract write-body típusai. */
type EmployeeUpdateBody = z.infer<typeof EmployeeUpdateBodySchema>;
type EmployeeCreateBody = z.infer<typeof EmployeeCreateBodySchema>;

/** A név-részekből (given family) számolt displayName, vagy null (ha egyik rész sincs). */
function toDisplayName(given: string | null, family: string | null): string | null {
  return [given, family].filter(Boolean).join(' ') || null;
}

/** Form-értékek → EDIT wire-body: az identity-mag (e-mail/név/becenév) PATCH-e + a munkaviszony-mezők.
 *  A `positionId` KÖTELEZŐ UUID-ként megy (az üres a resolvernél elbukik — NINCS üres→null map).
 *  A `nickname` DIRTY-alapú (`includeNickname`): érintetlen mező → `undefined` (KIMARAD, nem nulláz némán); piszkált
 *  üres → `null` (törlés), érték → beállítás. Default `true` (a resolver-validáláshoz). */
export function toEmployeeBody(
  v: EmployeeFormValues,
  opts?: { includeNickname?: boolean },
): EmployeeUpdateBody {
  const nn = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  const given = nn(v.givenName);
  const family = nn(v.familyName);
  const includeNickname = opts?.includeNickname ?? true;
  return {
    email: v.email.trim(),
    // a név KÖTELEZŐ: a vágott szöveg megy — az üres a szerződés `min(1)`-én bukik (mező-hiba), nem `null`
    givenName: v.givenName.trim(),
    familyName: v.familyName.trim(),
    displayName: toDisplayName(given, family),
    // undefined → a JSON.stringify kihagyja (nincs változás); piszkált üres → null (törlés); érték → beállítás.
    nickname: includeNickname ? nn(v.nickname) : undefined,
    // a telefon mindig megy: az Odin lista projektálja, tehát az előtöltés hű (üres → null = törlés)
    phone: nn(v.phone),
    // KÖTELEZŐ UUID (nincs üres→null map): üres esetén a resolver a positionId mezőn required-hibát ad.
    positionId: v.positionId,
    // HR-dátumok: üres → null (törlés), érték → beállítás. Nap-pontos string, nincs zóna-konverzió.
    birthDate: v.birthDate.trim() === '' ? null : v.birthDate.trim(),
    employmentStartDate: v.employmentStartDate.trim() === '' ? null : v.employmentStartDate.trim(),
    employmentEndDate: v.employmentEndDate.trim() === '' ? null : v.employmentEndDate.trim(),
    nameDay: v.nameDay.trim() === '' ? null : v.nameDay.trim(),
    // a TELJES lista megy (a sorrend és a törlés is így érvényesül); a név vágva — üresen a szerződés bukik rajta
    children: v.children.map((c) => ({ fullName: c.fullName.trim(), birthDate: c.birthDate })),
    employmentType: v.employmentType,
    employerKey: v.employerKey,
    ...probationBody(v),
  };
}

/** A próbaidő-blokk wire-alakja: kikapcsolva a dátumok `null`-ként mennek (törlés), bekapcsolva üres → `null`. */
function probationBody(v: EmployeeFormValues): {
  probation: boolean;
  probationStartDate: string | null;
  probationEndDate: string | null;
} {
  const day = (s: string): string | null => (v.probation && s.trim() !== '' ? s.trim() : null);
  return {
    probation: v.probation,
    probationStartDate: day(v.probationStartDate),
    probationEndDate: day(v.probationEndDate),
  };
}

/** Form-értékek → CREATE wire-body: az e-mail + név-részek + a munkaviszony-mezők (TISZTA GLOBÁLIS, szerep nélkül). */
export function toEmployeeCreateBody(v: EmployeeFormValues): EmployeeCreateBody {
  const nn = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  const given = nn(v.givenName);
  const family = nn(v.familyName);
  return {
    email: v.email.trim(),
    // a név KÖTELEZŐ: a vágott szöveg megy — az üres a szerződés `min(1)`-én bukik (mező-hiba), nem `null`
    givenName: v.givenName.trim(),
    familyName: v.familyName.trim(),
    // displayName a név-részekből (given family), ha van legalább egy rész — a lista ezt is mutathatja.
    displayName: toDisplayName(given, family),
    // Becenév: üres → null (a create-nél nincs mit „nem-nullázni"; a szerver üres/null-t nem ír profil-mezőnek).
    nickname: nn(v.nickname),
    phone: nn(v.phone),
    // KÖTELEZŐ UUID (nincs üres→null map): üres esetén a resolver a positionId mezőn required-hibát ad.
    positionId: v.positionId,
    // HR-dátumok: üres → null (törlés), érték → beállítás. Nap-pontos string, nincs zóna-konverzió.
    birthDate: v.birthDate.trim() === '' ? null : v.birthDate.trim(),
    employmentStartDate: v.employmentStartDate.trim() === '' ? null : v.employmentStartDate.trim(),
    employmentEndDate: v.employmentEndDate.trim() === '' ? null : v.employmentEndDate.trim(),
    nameDay: v.nameDay.trim() === '' ? null : v.nameDay.trim(),
    // a TELJES lista megy (a sorrend és a törlés is így érvényesül); a név vágva — üresen a szerződés bukik rajta
    children: v.children.map((c) => ({ fullName: c.fullName.trim(), birthDate: c.birthDate })),
    employmentType: v.employmentType,
    employerKey: v.employerKey,
    ...probationBody(v),
    // KEZDŐ JELSZÓ: NINCS trim (a jelszó byte-hű — egy szándékos szóköz is a jelszó része); üres → a kulcs
    // KIMARAD (`undefined`), tehát nem küldünk üres jelszót a szervernek.
    password: v.password === '' ? undefined : v.password,
  };
}

/**
 * Lokalizált zod-hibatérkép: a zod-issue (mező-path + kód) → a `validation:employees.*` réteg üzenete.
 * Ismeretlen issue → generikus (zod default) üzenet. A JELSZÓ tartalmi szabályát NEM itt validáljuk: annak
 * EGYETLEN autoritása a szerver (a UI a 422 gépi kódjait fordítja) — a contract itt csak hossz-korlátot ad.
 */
export function buildEmployeeErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    const field = issue.path[0];
    if (field === 'email') {
      return { message: t('validation:employees.email') };
    }
    if (field === 'password') {
      return { message: t('validation:employees.password.tooLong') };
    }
    if (field === 'positionId') {
      return { message: t('validation:employees.positionId.invalid') };
    }
    if (field === 'familyName' || field === 'givenName') {
      return { message: t(`validation:employees.${field}`) };
    }
    if (field === 'probationEndDate' || field === 'probationStartDate') {
      const reason = issue.code === 'custom' ? issue.params?.reason : undefined;
      if (reason === 'probation_end_before_start' || reason === 'probation_off') {
        return { message: t(`validation:employees.${reason}`) };
      }
    }
    if (field === 'children') {
      const sub = issue.path[2];
      if (sub === 'fullName' || sub === 'birthDate') {
        return { message: t(`validation:people.children.${sub}`) };
      }
    }
    return { message: ctx.defaultError };
  };
}

/**
 * RHF-resolver a contract-sémából: a validálás a WIRE-alakon fut (a `toEmployeeBody` normalizálása után), a
 * hiba-pathok 1:1 a form-mezők nevei (az üres positionId a required-uuid-n bukik → a positionId mezőre vezetve). Sikerkor az EREDETI
 * (string-alapú) form-értékek mennek tovább (a wire-body-t a mutáció építi). A minta a buildAssetResolver.
 */
export function buildEmployeeResolver(t: TFunction): Resolver<EmployeeFormValues> {
  const base = zodResolver(EmployeeUpdateBodySchema, {
    errorMap: buildEmployeeErrorMap(t),
  }) as unknown as Resolver<EmployeeFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toEmployeeBody(values) as unknown as EmployeeFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) {
      return { values: {}, errors: result.errors };
    }
    return { values, errors: {} };
  };
}

/** CREATE-resolver: a validálás az `EmployeeCreateBodySchema` WIRE-alakján fut (e-mail + munkaviszony-mezők);
 *  a hiba-pathok 1:1 a form-mezők. Sikerkor az EREDETI form-értékek mennek tovább (a body-t a mutáció építi). */
export function buildEmployeeCreateResolver(t: TFunction): Resolver<EmployeeFormValues> {
  const base = zodResolver(EmployeeCreateBodySchema, {
    errorMap: buildEmployeeErrorMap(t),
  }) as unknown as Resolver<EmployeeFormValues>;
  return async (values, context, options) => {
    const result = await base(
      toEmployeeCreateBody(values) as unknown as EmployeeFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) {
      return { values: {}, errors: result.errors };
    }
    return { values, errors: {} };
  };
}
